import {
  StudentProgress,
  StudentAchievement,
  StudentAssessmentEvent,
  ApiResponse,
  PaginatedResponse,
} from '@court-sync/shared';
import { apiGet, apiPost, apiPut } from './apiClient';

/** Default page size for assessment history (must match backend default). */
export const ASSESSMENT_EVENTS_PAGE_SIZE = 10;

export const studentProgressService = {
  async getStudentProgress(
    studentId: string,
    academyId: string,
    options?: { academySportId?: string }
  ): Promise<ApiResponse<StudentProgress | null>> {
    return apiGet<StudentProgress | null>(`/students/${studentId}/progress`, {
      params: {
        academyId,
        ...(options?.academySportId ? { academySportId: options.academySportId } : {}),
      },
    });
  },

  async getAssessmentEvents(
    studentId: string,
    academyId: string,
    pagination?: { page?: number; limit?: number }
  ): Promise<PaginatedResponse<StudentAssessmentEvent>> {
    const page = pagination?.page ?? 1;
    const limit = pagination?.limit ?? ASSESSMENT_EVENTS_PAGE_SIZE;
    return apiGet<StudentAssessmentEvent[]>(
      `/students/${studentId}/assessment-events`,
      { params: { academyId, page, limit } }
    ) as Promise<PaginatedResponse<StudentAssessmentEvent>>;
  },

  async createProgress(data: {
    studentId: string;
    academyId: string;
    academySportId?: string;
    skillLevel: 'beginner' | 'intermediate' | 'advanced';
    skillRatings?: Partial<Record<string, number>>;
    notes?: string;
  }): Promise<ApiResponse<{ id: string }>> {
    return apiPost<{ id: string }>(`/students/${data.studentId}/progress`, data);
  },

  async updateProgress(
    studentId: string,
    data: {
      academyId: string;
      academySportId?: string;
      skillLevel?: 'beginner' | 'intermediate' | 'advanced';
      skillRatings?: Partial<Record<string, number>>;
      notes?: string;
      lastAssessmentDate?: Date;
    }
  ): Promise<ApiResponse> {
    return apiPut(`/students/${studentId}/progress`, data);
  },

  async addAchievement(
    studentId: string,
    data: {
      academyId: string;
      title: string;
      description?: string;
      category: 'skill' | 'participation' | 'tournament' | 'improvement' | 'other';
    }
  ): Promise<ApiResponse<StudentAchievement>> {
    return apiPost<StudentAchievement>(`/students/${studentId}/progress/achievements`, data);
  },
};
